import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Gavel as BrandIcon,
  MailOutline as MailIcon,
  LockOutlined as LockIcon,
} from '@mui/icons-material';
import { getAuth, signInWithEmailAndPassword } from 'firebase/auth';
import { toast } from 'react-toastify';

const NAVY = '#1E3A5F';
const ACCENT = '#3B82F6';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const auth = getAuth();
      await signInWithEmailAndPassword(auth, email, password);
      toast.success('Welcome back');
      navigate('/');
    } catch (err) {
      console.error('Login error:', err);
      setError('Invalid email or password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        px: { xs: 4, sm: 6 },
        py: 8,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Subtle radial background accent */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `radial-gradient(circle at 20% 0%, ${ACCENT}10 0%, transparent 40%), radial-gradient(circle at 100% 100%, ${NAVY}10 0%, transparent 40%)`,
          pointerEvents: 'none',
        }}
      />

      <Box
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: 420,
        }}
      >
        {/* Brand */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            mb: 6,
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: NAVY,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
              boxShadow: '0 4px 12px rgba(30,58,95,0.25)',
            }}
          >
            <BrandIcon sx={{ fontSize: 24, color: '#FFFFFF' }} />
          </Box>
          <Typography
            sx={{
              fontSize: '1.25rem',
              fontWeight: 700,
              color: 'text.primary',
              letterSpacing: '-0.01em',
            }}
          >
            Huduma
          </Typography>
          <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
            Client Records
          </Typography>
        </Box>

        <Paper
          sx={{
            p: { xs: 6, sm: 7 },
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 3,
            boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
          }}
        >
          <Typography
            component="h1"
            sx={{
              fontSize: '1.25rem',
              fontWeight: 600,
              color: 'text.primary',
              mb: 0.5,
            }}
          >
            Welcome back
          </Typography>
          <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary', mb: 5 }}>
            Sign in to your account to continue
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 4 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleLogin} noValidate>
            <Box sx={{ mb: 3 }}>
              <Typography
                component="label"
                htmlFor="login-email"
                sx={{
                  display: 'block',
                  fontSize: '0.8125rem',
                  fontWeight: 500,
                  color: 'text.primary',
                  mb: 1,
                }}
              >
                Email
              </Typography>
              <TextField
                id="login-email"
                fullWidth
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                autoComplete="email"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MailIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box sx={{ mb: 5 }}>
              <Typography
                component="label"
                htmlFor="login-password"
                sx={{
                  display: 'block',
                  fontSize: '0.8125rem',
                  fontWeight: 500,
                  color: 'text.primary',
                  mb: 1,
                }}
              >
                Password
              </Typography>
              <TextField
                id="login-password"
                fullWidth
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setShowPassword((s) => !s)}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        tabIndex={-1}
                      >
                        {showPassword ? (
                          <VisibilityOffIcon sx={{ fontSize: 18 }} />
                        ) : (
                          <VisibilityIcon sx={{ fontSize: 18 }} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={loading || !email || !password}
              startIcon={
                loading ? <CircularProgress size={16} sx={{ color: '#FFFFFF' }} /> : null
              }
              sx={{ minHeight: 44 }}
            >
              {loading ? 'Signing in…' : 'Sign In'}
            </Button>
          </Box>
        </Paper>

        <Typography
          sx={{
            mt: 4,
            textAlign: 'center',
            fontSize: '0.75rem',
            color: 'text.secondary',
          }}
        >
          Secured by Firebase Authentication
        </Typography>
      </Box>
    </Box>
  );
};

export default Login;
